import {useLocation, useNavigate} from "react-router-dom";
import {Center, CloseButton, Container, Group, Loader, Paper, Space, Title} from "@mantine/core";
import {
  adjustToTimeZone,
  CustomFieldDef,
  customFieldDefSelector,
  getObjectId,
  useGetCustomFieldDefQuery
} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {useCallback, useMemo, useState} from "react";
import {skipToken} from "@reduxjs/toolkit/query";
import {
  CustomFieldDefForm,
  CustomFieldDefFormDefaultValues,
  CustomFieldDefFormValues
} from "../../components/form/CustomFieldDefForm/CustomFieldDefForm";
import {useCustomFieldDefMutation} from "../../hooks/useCustomFieldDefMutation";
import {ProtectedPage} from "../../components/page/ProtectedPage/ProtectedPage";
import {toFormDefaultsFromCustomFieldDef} from "../../components/form/CustomFieldDefForm/useCustomFieldDefForm";

type Props = {
  id?: string;
  copy?: boolean;
  formDefaults?: CustomFieldDefFormDefaultValues
}

export const CustomFieldDefFormPageRoute = () => {
  const {state} = useLocation();
  const id = state?.id;
  const copy = state?.copy;
  const formDefaults = state?.formDefaults;
  return (
      <CustomFieldDefFormPage
          id={id}
          copy={copy}
          formDefaults={formDefaults}
      />
  );
};

export const CustomFieldDefFormPage = (props: Props) => {
  const {
    id,
    copy = false,
    formDefaults,
  } = props;

  const navigate = useNavigate();
  const {t} = useTranslation(["common"]);

  const [access, setAccess] = useState(false);

  const selectCustomFieldDef = useMemo(() => {
    return customFieldDefSelector()
  }, [])

  const {
    currCustomFieldDef,
    isLoading
  } = useGetCustomFieldDefQuery(access ? (id ?? skipToken) : skipToken, {
    selectFromResult: (result) => ({
      currCustomFieldDef: selectCustomFieldDef(result),
      isLoading: result.isLoading
    })
  });

  const _formDefaults: CustomFieldDefFormDefaultValues = useMemo(() => {
    return formDefaults ? formDefaults : toFormDefaultsFromCustomFieldDef(currCustomFieldDef);
  }, [currCustomFieldDef, formDefaults]);

  const isEdit = useMemo(() => {
    return currCustomFieldDef && !copy
  }, [currCustomFieldDef, copy])

  const title: string = useMemo(() => {
    if (isEdit) {
      return t("common.page.entity.customfielddef.form.title.edit", "Edit Custom Field Definition", {ns: "common"});
    } else {
      return t("common.page.entity.customfielddef.form.title.new", "New Custom Field Definition", {ns: "common"});
    }
  }, [isEdit, t]);

  const {
    createCustomFieldDef,
    updateCustomFieldDef,
    isCreating,
    isUpdating,
  } = useCustomFieldDefMutation()

  const onCustomFieldDefSubmit = useCallback((formValues: CustomFieldDefFormValues) => {
    const patch: Partial<CustomFieldDef> = {
      companyId: getObjectId(formValues.company),
      forObjectType: formValues.objectType,
      code: formValues.code,
      dataType: formValues.dataType?.code,
      refObjectType: formValues.refObjectType,
      required: formValues.required ?? false,
      label: formValues.label,
    }

    if (isEdit) {
      const profile: Partial<CustomFieldDef> = {
        ...currCustomFieldDef.asTypeValue(),
        ...patch,
        lastModifiedDttm: adjustToTimeZone().getTime(),
      };
      updateCustomFieldDef(profile, {onSuccess: () => navigate(-1)})
    } else {
      const profile: Partial<CustomFieldDef> = {
        ...patch,
      };
      createCustomFieldDef(profile, {onSuccess: () => navigate(-1)})
    }
  }, [isEdit, currCustomFieldDef, updateCustomFieldDef, navigate, createCustomFieldDef]);

  return (
      <ProtectedPage
          entity={currCustomFieldDef}
          permissions={[isEdit ? "customfielddef_edit" : "customfielddef_create"]}
          isLoading={isLoading}
          onAccess={setAccess}>
        {(classes) => (
            <Container className={classes.container} fluid={true} py="md">
              <Center>
                {isLoading && <Loader/>}
                {!isLoading && (
                    <Paper miw={500} shadow="sm" p="md" py="md">
                      <Group position="apart">
                        <Title align="center">
                          {title}
                        </Title>
                        <CloseButton title="Close" size="xl" iconSize={20} onClick={() => navigate(-1)}/>
                      </Group>
                      <Space h="md"/>
                      <CustomFieldDefForm
                          defaultValues={_formDefaults}
                          submitting={isCreating || isUpdating}
                          onSubmit={onCustomFieldDefSubmit}/>
                    </Paper>
                )}
              </Center>
            </Container>
        )}
      </ProtectedPage>
  );
};

